import { useMemo } from "react"
import { useQuery } from "urql"

import { FULL_LIST_LIMIT } from "@/constants/common"
import type { CompetitorProduct, Query } from "@/graphql/codegen/graphql"
import { CompetitorProductsListDocument } from "@/graphql/codegen/graphql"

/**
 * Hook for searching Competitor products with two modes:
 *
 * 1. Text Search Mode (when query is provided):
 *    - Searches competitor products by query string (chemical name, product ID, etc.)
 *    - Query is paused until user types at least 2 characters
 *    - Used when user types in search bar
 *
 * 2. ID Search Mode (when competitorProductId is provided):
 *    - Fetches a specific competitor product by its ID
 *    - Used when user selects a product from dropdown suggestions
 *    - Ensures the exact selected product is loaded into the table
 */
const useCompetitorProducts = ({
  query = "",
  competitorProductId,
  pause = false,
}: {
  query: string
  competitorProductId?: string
  pause?: boolean
}): {
  competitorProductsList: CompetitorProduct[]
  competitorProductsListFetching: boolean
} => {
  // Build filter based on search mode: ID lookup or text search
  const hasValidQuery = query && query.length > 1

  let filter = {}
  if (competitorProductId) {
    filter = { competitorProductId }
  } else if (hasValidQuery) {
    filter = { query }
  }

  const [{ data, fetching: competitorProductsListFetching }] = useQuery<Pick<Query, "competitorProduct">>({
    query: CompetitorProductsListDocument,
    variables: {
      input: {
        filter,
        limit: FULL_LIST_LIMIT,
        offset: 0,
      },
    },
    // Pause query if:
    // - Explicitly paused (e.g., when Akrochem tab is selected) OR
    // - No competitorProductId AND query is less than 2 characters (prevents unnecessary API calls)
    pause: pause || (!competitorProductId && query.length < 2),
  })

  const competitorProductsList = useMemo(() => {
    if (!data?.competitorProduct?.list) {
      return []
    }
    return data.competitorProduct.list.__typename === "CompetitorProductListSuccess"
      ? (data.competitorProduct.list.competitorProducts as CompetitorProduct[])
      : []
  }, [data])

  return {
    competitorProductsList,
    competitorProductsListFetching,
  }
}

export default useCompetitorProducts
